import { Component, ElementRef, EventEmitter, OnDestroy, OnInit, Output, ViewChild, ChangeDetectorRef, Input } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { take, takeWhile } from 'rxjs/operators';
import { Constants } from 'src/app/order/constants/constants';
import { DocumentCategory } from 'src/app/order/enums/document-category.enum';
import { FeeTypeEnum } from 'src/app/order/enums/fee-type.enum';
import { ModalDefinitions } from 'src/app/order/enums/modal-definitions.enum';
import { Messages } from 'src/app/order/messages/order.messages';
import { ClientService } from 'src/app/order/services/external/client-validation/client-validation.service';
import { FeeService } from 'src/app/order/services/external/fee/fee.service';
import { ProposalDraftService } from 'src/app/order/services/external/proposal-draft/proposal-draft.service';
import { ProposalService } from 'src/app/order/services/external/proposal/proposal.service';
import { SimulationService } from 'src/app/order/services/external/simulation/simulation.service';
import { DialogService } from 'src/app/order/services/internal/dialog/dialog.service';
import { LoadingService } from 'src/app/order/services/internal/loading/loading.service';
import { RoutingService } from 'src/app/order/services/internal/routing/routing.service';
import { ScrollService } from 'src/app/order/services/internal/scroll/scroll.service';
import { WizardService } from 'src/app/order/services/internal/wizard/wizard.service';
import { DataStoreService } from 'src/app/order/store/data-store.service';
import { DataStore } from 'src/app/order/store/data.store';
import { ApiResultModel } from 'src/app/shared/models/api-result.model';
import { FeeTypeModel, Proposal } from 'src/app/shared/models/proposal';
import { ProposalDraftQueryModel } from 'src/app/shared/models/proposal-draft-query.model';
import { Rate, RatesClientValidation } from 'src/app/shared/models/rates-client-validation.model';
import { MdrFeeModel } from 'src/app/shared/models/response/response-mdr-fee.model';
import { SimulationFeesModel, SimulationModel, SimulationServicesModel } from 'src/app/shared/models/simulation.model';
import { UserDataModel } from 'src/app/shared/models/user-data.model';
import { FdFieldConfigs } from 'src/app/shared/shared-components.module';
import { ConfigurationModel } from 'src/app/start/models/configuration/configuration.model';
import { ConfigurationService } from 'src/app/start/services/configuration.service';
import { FormStep } from '../../form-step';
import { FeeEditorDialogData, FeeEditorModalComponent } from '../../modals/fee-editor-modal/fee-editor-modal.component';
import { PrepaymentTableComponent } from './prepayment-table/prepayment-table.component';
import { PrepaymentService } from 'src/app/order/services/external/prepayment/prepayment.service';
import { FdFeeEditorConfig } from 'src/app/shared/fd-form-components/fd-fee-editor/fd-fee-editor.component';
import { FdRadioButtonConfig } from 'src/app/shared/fd-form-components/fd-radio-button/fd-radio-button.component';
import { Item } from 'src/app/shared/fd-form-components/fd-select/fd-select.component';

interface CellRef {
  index: number;
  model: MdrFeeModel;
}
type CellOrNumber = CellRef | number | any;

type LinhaPorBandeira = {
  flag: string;
  credito?: CellOrNumber;
  debito?: CellOrNumber;
  parceladoAte6?: CellOrNumber;
  parceladoMais6?: CellOrNumber;
  crediario?: CellOrNumber;
  voucher?: CellOrNumber;
};

type ColunaTransacao = 'credito' | 'debito' | 'parceladoAte6' | 'parceladoMais6' | 'crediario' | 'voucher';

@Component({
  selector: 'app-mdr-fee-edit',
  templateUrl: './mdr-fee-edit.component.html',
  styleUrls: ['./mdr-fee-edit.component.scss'],
  standalone: false,
})
export class MdrFeeEditComponent extends FormStep implements OnInit, OnDestroy {
  public STEP_NUMBER = 0;
  public readonly UNDER_MINIMUM_LIMIT_TEXT =
    'Devido ao fato da taxa informada ser menor que a taxa mínima, a proposta poderá não ser submetida ou aprovada';

  public configuration: ConfigurationModel;
  public dialogClosed: boolean = true;
  public formGroup: UntypedFormGroup;
  public fields: FdFieldConfigs;
  public fieldsArray: FdFieldConfigs[] = new Array<FdFieldConfigs>();
  public alive = true;
  public proposal: Proposal;
  public userData: UserDataModel;
  public tableInterval: number;
  public isCEF: boolean = this.dataStoreService.getCEFFlag();
  public dataSourceByFlag = new MatTableDataSource<LinhaPorBandeira>([]);

  public displayedColumnsByFlag: string[] = ['flag', 'credito', 'debito', 'parceladoAte6', 'parceladoMais6', 'crediario'];

  private readonly BASE_COLUMNS: ReadonlyArray<string> = ['flag', 'credito', 'debito', 'parceladoAte6', 'parceladoMais6', 'crediario'];

  private readonly FLAG_ORDER: string[] = ['VISA', 'MASTER', 'ELO', 'CABAL', 'AMEX'];

  private readonly FLAG_ALIASES: Record<string, string> = {
    VISA: 'VISA',
    MASTER: 'MASTER',
    CABAL: 'CABAL',
    ELO: 'ELO',
    AMEX: 'AMEX',
  };

  public mdrFees: MdrFeeModel[];
  public proposalMdrModel: MdrFeeModel[];

  public hasErrorGetRatesMdr: boolean = false;

  @Output() public backAction: EventEmitter<void> = new EventEmitter();

  @ViewChild('tooltip') public tooltip: MatTooltip;
  @ViewChild('row') public row: ElementRef;

  @ViewChild(PrepaymentTableComponent)
  public prepaymentComponent: PrepaymentTableComponent;

  @Input()
  public viewFlagImg: boolean = true;

  constructor(
    private formBuilder: UntypedFormBuilder,
    protected proposalService: ProposalService,
    protected loadingService: LoadingService,
    protected dialogService: DialogService,
    protected dataStore: DataStore,
    protected scrollService: ScrollService,
    protected wizardService: WizardService,
    protected matDialog: MatDialog,
    protected configurationService: ConfigurationService,
    protected routingService: RoutingService,
    protected dataStoreService: DataStoreService,
    private clientService: ClientService,
    private feeService: FeeService,
    private proposalDraftService: ProposalDraftService,
    private simulationService: SimulationService,
    private prepaymentService: PrepaymentService,
    private cdr: ChangeDetectorRef
  ) {
    super(
      dataStore,
      scrollService,
      wizardService,
      matDialog,
      proposalService,
      loadingService,
      dialogService,
      configurationService,
      routingService,
      dataStoreService
    );
  }

  public async ngOnInit() {
    super.ngOnInit();
  }

  public ngOnDestroy() {
    this.alive = false;
  }

  public get isPrepaymentEnabled(): boolean {
    return (
      this.proposal &&
      this.proposal.feeTypeModel &&
      this.proposal.feeTypeModel.enablePrepayment &&
      this.configuration &&
      this.configuration.ableAnticipation
    );
  }

  public get isAnticipationExternal(): boolean {
    return this.proposal && this.proposal.feeTypeModel && this.proposal.feeTypeModel.anticipationExternal;
  }

  public get isCnaeLoterica(): boolean {
    return this.proposal.offerDataDetails.cnae.toString() === '8299706';
  }

  public get formControls(): { [key: string]: AbstractControl } {
    return this.formGroup.controls;
  }

  private get feeFormsArray(): UntypedFormArray {
    return this.formControls.feeForms as UntypedFormArray;
  }

  public get feeFormsControls() {
    return this.feeFormsArray.controls as UntypedFormGroup[];
  }

  public get isCPF() {
    return (
      this.proposal &&
      this.proposal.registrationDataPersonalInfo &&
      this.proposal.registrationDataPersonalInfo.documentCategory === DocumentCategory.CPF
    );
  }

  public get hasSplitTech(): boolean {
    return this.proposal.productSelection.hasSplitTech;
  }

  public isPfacAccreditationType(): boolean {
    return this.proposal?.registrationDataPersonalInfo?.isPfac === true;
  }

  public createFields(): FdFieldConfigs {
    if (this.isPrepaymentEnabled) {
      return {
        ...this.prepaymentComponent.getFields,
        suggestedMdrFee: {
          controlName: 'suggestedMdrFee',
          maskCharsReplace: /[% ]/,
          label: 'Valor unitário',
          modalTitle: 'Indique a taxa do produto',
          childControl: {
            controlName: 'feeEdit',
            label: 'Taxa solicitada',
            isPercent: true,
            maxLength: 4,
            messages: {
              required: 'Informe o valor',
              invalid: 'Valor inválido',
              valueTooLow: 'O valor deve ser maior que o mínimo especificado',
              valueTooHigh: 'O valor deve ser menor que o máximo especificado',
            },
          },
        },
        receivingDeadline: {
          controlName: 'receivingDeadline',
          label: 'Qual é o prazo de liquidação?',
          items: [
            { value: 30, label: '30 dias' },
            { value: 31, label: '31 dias' },
          ],
          messages: {
            required: 'Informe o prazo de liquidação',
            invalid: 'Prazo de liquidação inválido',
          },
        } as FdRadioButtonConfig,
      };
    }
    return {
      suggestedMdrFee: {
        controlName: 'suggestedMdrFee',
        maskCharsReplace: /[% ]/,
        label: 'Valor unitário',
        modalTitle: 'Indique a taxa do produto',
        childControl: {
          controlName: 'feeEdit',
          label: 'Taxa solicitada',
          isPercent: true,
          maxLength: 4,
          messages: {
            required: 'Informe o valor',
            invalid: 'Valor inválido',
            valueTooLow: 'O valor deve ser maior que o mínimo especificado',
            valueTooHigh: 'O valor deve ser menor que o máximo especificado',
          },
        },
      },
      receivingDeadline: {
        controlName: 'receivingDeadline',
        label: 'Qual é o prazo de liquidação?',
        items: [
          { value: 30, label: '30 dias' },
          { value: 31, label: '31 dias' },
        ],
        messages: {
          required: 'Informe o prazo de liquidação',
          invalid: 'Prazo de liquidação inválido',
        },
      } as FdRadioButtonConfig,
    };
  }

  public createFormGroup(): UntypedFormGroup {
    if (!this.configuration) {
      this.configuration = this.configurationService.getConfigurationFromSessionStorage();
    }

    if (this.isPrepaymentEnabled) {
      return this.formBuilder.group({
        prepaymentForm: this.prepaymentComponent.getFormGroup(),
        feeForms: this.formBuilder.array([]),
        receivingDeadline: ['', this.isPfacAccreditationType() ? Validators.required : null],
      });
    }
    return this.formBuilder.group({
      feeForms: this.formBuilder.array([]),
      receivingDeadline: ['', this.isPfacAccreditationType() ? Validators.required : null],
    });
  }

  public async persistData() {
    const rawValue = this.formGroup.getRawValue();
    const feeTypeModel: FeeTypeModel = this.proposal?.feeTypeModel ? { ...this.proposal.feeTypeModel } : null;

    if (this.isPfacAccreditationType() && feeTypeModel) {
      feeTypeModel.receivingDeadline = rawValue.receivingDeadline;
    }

    if (this.isPrepaymentEnabled) {
      return await this.dataStore.updateProposal({
        prepaymentFeeModel: rawValue.prepaymentForm,
        mdrFeeModel: rawValue.feeForms,
        feeTypeModel,
      });
    }

    if (this.hasErrorGetRatesMdr) {
      return;
    }

    return await this.dataStore.updateProposal({ mdrFeeModel: rawValue.feeForms, feeTypeModel });
  }

  public getFormControlByIndex(index: number) {
    const formArray = this.feeFormsArray;
    return (formArray.controls[index] as UntypedFormGroup).controls;
  }

  public showTooltipAndStopPropagation() {
    this.tooltip.toggle();
  }

  public shouldShowAlert(i: number) {
    const formGroup = this.feeFormsControls[i];
    return (
      this.dialogClosed &&
      formGroup &&
      formGroup.controls &&
      formGroup.controls.feeEdit &&
      formGroup.controls.feeEdit.value !== null &&
      formGroup.controls.feeEdit.value !== undefined &&
      formGroup.controls.feeEdit.value !== '' &&
      formGroup.controls.minimumMdrFee &&
      formGroup.controls.feeEdit.value < formGroup.controls.minimumMdrFee.value
    );
  }

  public async onModalConfirm(data: FeeEditorDialogData) {
    data.formControl.setValue(data.formGroup.controls.feeEdit.value);
    const item = this.mdrFees.find((_, idx) => idx === data.rowIndex);
    (this.fieldsArray[data.rowIndex].suggestedMdrFee as FdFeeEditorConfig).isEdited =
      item.suggestedMdrFee !== data.formGroup.controls.feeEdit.value;
  }

  public openFeeEditorModal(dialogData: FeeEditorDialogData, feeIndex: number) {
    if (this.hasSplitTech) return;

    const relatedFormGroup = this.feeFormsArray.controls[feeIndex] as UntypedFormGroup;

    if (relatedFormGroup) {
      const rawValue = relatedFormGroup.getRawValue();
      if (rawValue && rawValue.suggestedMdrFee !== null && rawValue.suggestedMdrFee !== undefined) {
        const item = this.mdrFees.find((_, idx) => idx === feeIndex);

        if (item) {
          dialogData.title = dialogData.field.modalTitle;
          dialogData.itemDescription = `${item.flag} - ${item.transaction}`;
          dialogData.maxValue = item.suggestedMdrFee;
          dialogData.initialValue = dialogData.formControl.value || item.suggestedMdrFee;
          dialogData.minValue = item.minimumMdrFee;
          dialogData.rowIndex = feeIndex;
          dialogData.confirmCallbackFn = this.onModalConfirm.bind(this);

          const dialogRef = this.dialog.open(FeeEditorModalComponent, {
            disableClose: true,
            width: ModalDefinitions.DEFAULT_MODAL_WIDTH,
            panelClass: 'fd-fee-editor-dialog',
            data: dialogData,
          });

          dialogRef.afterClosed().subscribe(() => (this.dialogClosed = true));
          dialogRef.afterOpened().subscribe(() => (this.dialogClosed = false));
          return;
        }
      }
    }
  }

  public openMdrFlexFeeEditorModal(dialogData: FeeEditorDialogData, index: number) {
    this.openFeeEditorModal(dialogData, index);
  }

  public canEdit(_modality: any): boolean {
    return !this.hasSplitTech;
  }

  public addFeeForm(fee: Partial<MdrFeeModel> = {}) {
    this.fieldsArray.push(this.createFields());
    this.feeFormsArray.push(this.createFeeForm(fee));
  }

  public processMdrFees(mdrFeeModels: MdrFeeModel[]) {
    mdrFeeModels.forEach((item, idx) => {
      if (item.feeType == 'CURRENCY' && this.isCnaeLoterica) {
        item.tariffValue = item.suggestedMdrFee;
      }
      this.addFeeForm(item);
      (this.fieldsArray[idx].suggestedMdrFee as FdFeeEditorConfig).isEdited = this.isMdrEdited(item);
    });

    this.buildFlagView(!!this.proposalMdrModel ? this.proposalMdrModel : mdrFeeModels);
  }

  public isMdrEdited(fee: Partial<MdrFeeModel> = {}): boolean {
    if (fee) {
      const arrItem = this.mdrFees.find((val) => val.codeFlag === fee.codeFlag && val.modality === fee.modality);
      if (!fee || !fee.suggestedMdrFee || !arrItem || !arrItem.suggestedMdrFee) return false;
      return fee.suggestedMdrFee !== arrItem.suggestedMdrFee;
    }
    return false;
  }

  public saveSimulation() {
    if (!this.isFormGroupValid()) return;

    this.dataStore.setStepStatus(this.STEP_NUMBER, this.formGroup.valid);
    this.persistData().then((persisted) => {
      if (persisted) {
        this.dataStore
          .getProposal()
          .pipe(take(1))
          .pipe(takeWhile(() => this.alive))
          .toPromise()
          .then(
            (proposal) => {
              this.proposal = proposal;
              if (this.dataStoreService.isBinServiceContractProposal(this.proposal.serviceContract)) {
                this.validateRates();
              } else {
                this.submitSimulation();
              }
            },
            () => {}
          );
      }
    });
  }

  public buildFeeObject(): SimulationFeesModel[] {
    return this.proposal.mdrFeeModel.map(
      (item) =>
        ({
          idService: item.serviceId && item.serviceId.toString(),
          minimumMdrFee: item.minimumMdrFee,
          sentMdrFee: item.feeEdit,
          suggestedMdrFee: item.suggestedMdrFee,
          modality: item.modality,
          transactionDescription: item.transaction,
        } as SimulationFeesModel)
    );
  }

  public buildServiceObject(): SimulationServicesModel[] {
    return this.proposal.productSelection.products.map(
      (item) =>
        ({
          idService: item.technology,
          technologyAmount: item.amount,
          technologyPrice: Number(item.price.toString().replace(/[Rr$\s]/g, '')),
        } as SimulationServicesModel)
    );
  }

  public deleteDraft() {
    const activeDraft = localStorage.getItem(Constants.ACTIVE_DRAFT_SESSIONSTORAGE_KEY);

    this.proposalDraftService.deleteDraft(this.createRequestObject(activeDraft)).subscribe(
      () => this.dialogService.openDialog(Messages.SIMULATION_SAVE_SUCCESS, () => this.routingService.navigateToHome()),
      (error) => {
        let errorArg = error;
        if (error.error && typeof error.error === 'object') {
          errorArg = error.error;
          if (errorArg.field) errorArg = errorArg.field;
          else if (error.error instanceof ProgressEvent) errorArg = error.message;
        }
        this.dialogService.openErrorDialog(Messages.SIMULATION_SAVE_ERROR, errorArg, () => this.routingService.navigateToHome());
      }
    );
  }

  public createRequestObject(externalDraftUuid): ProposalDraftQueryModel {
    return { externalDraftUuid };
  }

  public submitSimulation() {
    const userData = this.dataStoreService.getUserData();
    if (!userData) return;

    const model: SimulationModel = {
      agentCpfCnpj: userData.cpfCnpj,
      billingValue: this.proposal.offerDataDetails.annualRevenue,
      cnaeNumber: Number(this.proposal.offerDataDetails.cnae),
      cpfCnpj: this.proposal.registrationDataPersonalInfo.cpfCnpj,
      email: this.isCPF ? this.proposal.registrationDataPersonalInfo.personEmail : this.proposal.registrationDataPersonalInfo.companyEmail,
      fees: this.buildFeeObject(),
      services: this.buildServiceObject(),
    };

    if (model && this.isPrepaymentEnabled) {
      model.suggestedPrepaymentFee =
        this.isPrepaymentEnabled && this.proposal.prepaymentFeeModel
          ? (this.proposal.prepaymentFeeModel as any).suggestedPrepaymentFee
          : null;
      model.sentPrepaymentFee =
        this.isPrepaymentEnabled && this.proposal.prepaymentFeeModel ? (this.proposal.prepaymentFeeModel as any).feeEdit : null;
    }

    this.simulationService.saveSimulation(model).subscribe(
      () => this.deleteDraft(),
      (error) => {
        let errorArg = error;
        if (error.error && typeof error.error === 'object') {
          errorArg = error.error;
          if (errorArg.field) errorArg = errorArg.field;
          else if (error.error instanceof ProgressEvent) errorArg = error.message;
        }
        this.dialogService.openErrorDialog(Messages.SIMULATION_SAVE_ERROR, errorArg, () => this.routingService.navigateToHome());
      }
    );
  }

  public next(): void {
    this.submitted = true;
    if (this.dataStoreService.isBinServiceContractProposal(this.proposal.serviceContract)) {
      this.validateRates();
    } else {
      super.next();
    }
  }

  public validateRates() {
    const data: RatesClientValidation = this.buildValidationRatesData();
    this.clientService.validateRates(data).subscribe(
      (response) => {
        if (!response) {
          this.dialogService.openDialog(Messages.RATES_INVALID_SIPAG);
        } else {
          this.isCEF ? this.submitSimulation() : this.nextPage();
        }
      },
      (err) => {
        this.erroValidateRates(err);
      }
    );
  }

  public buildValidationRatesData(): RatesClientValidation {
    const ratesList: Rate[] = new Array();
    const rawValue = this.formGroup.getRawValue();
    const mdrFeeModel: MdrFeeModel[] = rawValue.feeForms;

    mdrFeeModel.forEach((mdr) => {
      const rate: Rate = {
        acquiringServiceId: mdr.serviceId.toString(),
        codeFlag: mdr.codeFlag,
        modality: mdr.modality,
        rate: mdr.feeEdit ? mdr.feeEdit.toString() : mdr.suggestedMdrFee.toString(),
      };
      ratesList.push(rate);
    });

    const data: RatesClientValidation = {
      cnae: this.proposal.offerDataDetails.cnae,
      document: this.proposal.registrationDataPersonalInfo.cpfCnpj,
      rateCategory: FeeTypeEnum.MDR,
      subChannel: this.proposal.subChannel,
      ratesList,
    };
    return data;
  }

  private isEnablePrepayment = false;

  public async updateFields(proposal: Proposal) {
    if (this.isPfacAccreditationType() && proposal?.feeTypeModel?.receivingDeadline) {
      this.formControls.receivingDeadline.setValue(proposal.feeTypeModel.receivingDeadline);
    }

    this.proposalMdrModel = proposal.mdrFeeModel;
    let mdrErro = true;
    let prepaymentErro = true;
    this.isEnablePrepayment = false;
    let isAnticipationExternal = false;
    if (proposal.feeTypeModel) {
      this.isEnablePrepayment = proposal.feeTypeModel.enablePrepayment ?? false;
      isAnticipationExternal = proposal.feeTypeModel.anticipationExternal ?? false;
    }
    try {
      const responseMdrFees = await this.feeService
        .getMdrFees(
          this.proposal.offerDataDetails.cnae,
          this.proposal.registrationDataAddress.state,
          this.proposal.offerDataDetails.campaign,
          this.proposal.offerDataDetails.revenueRange,
          this.proposal.registrationDataPersonalInfo.documentCategory === 'CPF' ? 'P' : 'L',
          this.isEnablePrepayment
        )
        .toPromise();

      this.mdrFees = responseMdrFees;
      this.processMdrFees(!!this.proposalMdrModel ? this.proposalMdrModel : responseMdrFees);

      mdrErro = false;

      if (this.isEnablePrepayment) {
        const responsePrepayment = await this.feeService
          .getPrepaymentFee(
            proposal.offerDataDetails.cnae,
            proposal.offerDataDetails.revenueRange,
            proposal.offerDataDetails.campaign,
            isAnticipationExternal
          )
          .toPromise();
        this.prepaymentComponent.updatePrepayment(responsePrepayment, proposal);
      }

      prepaymentErro = false;
    } catch (err) {
      this.hasErrorGetRatesMdr = true;
      const serializedError = err.error ? (err.error as ApiResultModel) : null;
      this.erroMdr(serializedError, mdrErro, prepaymentErro);
    }
  }

  protected proposalGetCallback(): void {
    this.userData = this.dataStoreService.getUserData();

    if (!this.proposal || !this.proposal.offerDataDetails || !this.proposal.feeTypeModel) {
      this.dialogService.openErrorDialog(
        Messages.FAILED_TO_GET_PROPOSAL_DATA,
        'Api Fiserv Online - Taxas MDR',
        () => this.back(),
        null,
        'error'
      );
    }

    this.configuration = this.configurationService.getConfigurationFromSessionStorage();
  }

  public back(saveData: boolean = true): void {
    if (this.isFormGroupValid() && saveData) {
      this.persistData().then((persisted) => {
        if (persisted) {
          this.wizardService.previousStep();
        }
      });
    } else {
      this.wizardService.previousStep();
    }
  }

  private createFeeForm(product: Partial<MdrFeeModel>): UntypedFormGroup {
    return this.formBuilder.group({
      suggestedMdrFee: [product.suggestedMdrFee],
      feeEdit: [product.feeEdit],
      codeFlag: [product.codeFlag],
      flag: [product.flag],
      minimumMdrFee: [product.minimumMdrFee],
      transaction: [product.transaction],
      modality: [product.modality],
      serviceId: [product.serviceId],
      feeType: [product.feeType],
      anticipationVoucherDisabled: [product.anticipationVoucherDisabled],
      completeVoucherSubscription: [product.completeVoucherSubscription],
    });
  }

  private nextPage(): void {
    this.submitted = true;

    if (this.isFormGroupValid()) {
      this.dataStore.setStepStatus(this.STEP_NUMBER, this.formGroup.valid);
      this.persistData()
        .then((persisted) => {
          if (persisted) {
            this.wizardService.nextStep();
          }
        })
        .finally(() => {});
    }
  }

  private erroValidateRates(err: any) {
    let message = '';
    let obj: any = err.error.details;
    if (err.error && err.error.details) {
      if (obj instanceof Object) {
        const details = err.error.details;
        if (details && details.mensagem) {
          message += details.mensagem;
          obj = '';
        } else {
          message += JSON.stringify(err.error.details);
        }
      } else {
        message += obj;
      }
    } else if (err.error && err.error.message) {
      message += err.error.message;
    }

    if (message.length > 0) {
      if (obj instanceof Object) {
        if (this.isCEF) {
          this.dialogService.openErrorDialog(Messages.GENERIC_ERROR, JSON.stringify(err.error.details), () =>
            this.routingService.navigateToHome()
          );
          return;
        }
        this.dialogService.openErrorDialog(Messages.GENERIC_ERROR, JSON.stringify(err.error.details));
      } else {
        if (this.isCEF) {
          this.dialogService.openDialogWithMessage(message, () => this.routingService.navigateToHome());
          return;
        }
        this.dialogService.openDialogWithMessage(message);
      }
    } else {
      this.dialogService.openDialog(Messages.GENERAL_ERROR);
    }
  }

  private erroMdr(serializedError: ApiResultModel, mdrErro: boolean, prepaymentErro: boolean) {
    if (serializedError && serializedError.message) {
      if (this.isCEF) {
        this.dialogService.openDialogWithMessage(serializedError.message, () => this.routingService.navigateToHome());
      } else {
        this.dialogService.openDialogWithMessage(serializedError.message, () => this.back(false));
      }
    } else {
      if (this.isCEF) {
        if (mdrErro) {
          this.dialogService.openDialog(Messages.FAILED_TO_GET_MDR_FEES, () => this.routingService.navigateToHome());
        }
        if (prepaymentErro) {
          this.dialogService.openDialog(Messages.FAILED_TO_GET_PREPAYMENT_FEE, () => this.routingService.navigateToHome());
        }
      } else {
        if (mdrErro) {
          this.dialogService.openDialog(Messages.FAILED_TO_GET_MDR_FEES, () => this.back(false));
        }
        if (prepaymentErro) {
          this.dialogService.openDialog(Messages.FAILED_TO_GET_PREPAYMENT_FEE, () => this.backAction.emit());
        }
      }
    }
  }

  private refreshDisplayedColumnsByFlag(): void {
    const shouldShowVoucher = this.hasVoucher;

    const next = shouldShowVoucher ? [...this.BASE_COLUMNS, 'voucher'] : [...this.BASE_COLUMNS];

    const changed = next.length !== this.displayedColumnsByFlag.length || next.some((c, i) => c !== this.displayedColumnsByFlag[i]);

    if (changed) {
      this.displayedColumnsByFlag = next;
      this.cdr.detectChanges();
    }
  }

  private tooltipVoucher: string;
  public hasVoucherMsg: boolean = true;

  public getTextOfModalVoucher(): string {
    if (this.tooltipVoucher && this.tooltipVoucher.length) {
      return this.tooltipVoucher;
    }
    this.hasVoucherMsg = false;
    const tooltipMsg = [];
    const fees = this.dataSourceByFlag?.data || [];

    for (const fee of fees) {
      if (fee.voucher) {
        if (fee.voucher.model.anticipationVoucherDisabled && this.isEnablePrepayment) {
          tooltipMsg.push(Constants.VOUCHER_AINTICIPATION_DISABLE_TOOLTIP_MSG);
          this.hasVoucherMsg = true;
        }
        if (fee.voucher.model.completeVoucherSubscription) {
          tooltipMsg.push(Constants.VOUCHER_COMPLETE_SUBSCRIPTION_TOOLTIP_MSG);
          this.hasVoucherMsg = true;
        }
        break;
      }
    }
    this.tooltipVoucher = tooltipMsg.join('\n\n');
    return this.tooltipVoucher;
  }

  private normalizeFlag(flag: string): string {
    return (flag || '').trim().replace(/\s+/g, ' ');
  }

  private buildFlagView(mdrFeeModels: MdrFeeModel[]): void {
    if (!mdrFeeModels || mdrFeeModels.length === 0) {
      this.dataSourceByFlag.data = [];
      this.refreshDisplayedColumnsByFlag();
      this.cdr.detectChanges();
      return;
    }

    const txKeyMap: Record<string, ColunaTransacao> = {
      Crédito: 'credito',
      Débito: 'debito',
      'Parcel. até 6x': 'parceladoAte6',
      'Parcel. maior que 6x': 'parceladoMais6',
      Crediário: 'crediario',
      Voucher: 'voucher',
    };

    const byFlag = new Map<string, LinhaPorBandeira>();

    for (let idx = 0; idx < mdrFeeModels.length; idx++) {
      const r = mdrFeeModels[idx];

      const displayFlag = this.normalizeFlag(r.flag);
      const canonical = this.canonicalFlagName(displayFlag);

      if (!byFlag.has(canonical)) {
        byFlag.set(canonical, {
          flag: displayFlag,
          credito: null,
          debito: null,
          parceladoAte6: null,
          parceladoMais6: null,
          crediario: null,
          voucher: null,
        });
      }

      const row = byFlag.get(canonical)!;
      const txRaw = (r.transaction || '').trim();
      const key = txKeyMap[txRaw] as ColunaTransacao | undefined;

      if (key) {
        row[key] = { index: idx, model: r } as CellRef;
      }
    }

    const priorityIndex = (canonical: string): number => {
      const idx = this.FLAG_ORDER.indexOf(canonical);
      return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
    };

    const rows = Array.from(byFlag.entries())
      .sort(([aKey, aVal], [bKey, bVal]) => {
        const pa = priorityIndex(aKey);
        const pb = priorityIndex(bKey);
        if (pa !== pb) return pa - pb;

        return (aVal.flag || '').localeCompare(bVal.flag || '');
      })
      .map(([, v]) => v);

    this.dataSourceByFlag.data = rows;
    this.refreshDisplayedColumnsByFlag();
    this.cdr.detectChanges();
  }

  private canonicalFlagName(raw: string): string {
    const base = (raw || '').trim().replace(/\s+/g, ' ').toUpperCase();
    return this.FLAG_ALIASES[base] || base;
  }

  public get hasVoucher(): boolean {
    const data = this.dataSourceByFlag?.data || [];
    return data.some((r) => r?.voucher !== null && r?.voucher !== undefined);
  }

  getFlagImageURL(flagName: string) {
    return '/assets/images/simulator-flags/' + flagName.toUpperCase() + '.png';
  }
}
